import { type FC, forwardRef, useId } from "react";
import { useFormState } from "react-hook-form";
import { twMerge } from "tailwind-merge";

import { ErrorMessageBanner } from "./ErrorMessageBanner";

import { t } from "i18n/i18n";

type LabeledTextInputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  wrapperClassName?: string;
  name: string;
  label: string;
  postLabel?: React.ReactNode;
  required?: boolean;
};

export const LabeledTextInput: FC<LabeledTextInputProps> = forwardRef<
  HTMLInputElement,
  LabeledTextInputProps
>(function LabeledTextInput(
  { id: idProp, className, wrapperClassName, name, label, postLabel, required = false, ...props },
  ref,
) {
  const randomId = useId();
  const formState = useFormState({ name });
  const id = idProp || `${randomId}-${name}`;

  const labelNode = (
    <label className="font-medium text-black-tint-20" htmlFor={id}>
      {label}
    </label>
  );

  return (
    <div className={twMerge("flex flex-col", wrapperClassName)}>
      <div className="flex items-center justify-between">
        {postLabel ? (
          <div className="flex items-center gap-2">
            {labelNode}
            {postLabel}
          </div>
        ) : (
          labelNode
        )}
        {!required && <div className="font-normal text-black-tint-40">{t("common.optional")}</div>}
      </div>
      <input
        aria-errormessage={formState.errors[name] ? `${id}-error` : undefined}
        aria-invalid={formState.errors[name] ? "true" : "false"}
        className={twMerge(
          "rounded border border-black-tint-90 px-1.5 py-1 font-normal aria-invalid:border-red",
          className,
        )}
        id={id}
        name={name}
        ref={ref}
        {...props}
      />
      <ErrorMessageBanner id={`${id}-error`} name={name} />
    </div>
  );
});
